import { useTranslation } from 'react-i18next';
import { Check } from 'lucide-react';
import { useEntwurf } from '@/entwurf/EntwurfOptionen';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/EmptyState';
import { NextStepCta } from '@/components/journey/NextStepCta';

type EmptyVariant =
  | 'no-feed'
  | 'no-optimized'
  | 'no-filter-results'
  | 'approved-awaiting-upload'
  | 'no-approved'
  | 'no-rejected'
  | 'all-reviewed';

interface WorkbenchEmptyStateProps {
  variant: EmptyVariant;
  sessionAccepted?: number;
  sessionRejected?: number;
  /** Einzige Handlung der Variante 'no-filter-results': Filter zuruecksetzen. */
  onResetFilters?: () => void;
  /** Variante 'approved-awaiting-upload': wieviele Produkte auf den Upload warten. */
  approvedAwaitingUpload?: number;
  /** Variante 'approved-awaiting-upload': Anteil der letzten Sammelfreigabe daran. */
  justBulkReleased?: number;
  /**
   * Erfolgsmoment-Schalter: wie viele Produkte der Reiter "Freigegeben"
   * insgesamt zaehlt. NULL heisst "Zaehler unbekannt", nicht 0 - die Zeile
   * faellt dann weg (dieselbe Haltung wie bei den Reiterzahlen selbst).
   */
  approvedTotal?: number | null;
  /**
   * Erfolgsmoment-Schalter: Feed-Durchschnittsscore aus dem juengsten
   * feed_health_snapshots-Befund. NULL = kein Befund, Zeile faellt weg.
   */
  avgScore?: number | null;
  /**
   * Oeffnet den Upload-Dialog DIESER Seite (Spec 2026-09-16, Abschnitt 7).
   *
   * Bis dahin schickte der Leerzustand den Nutzer per TEXT in den Reiter
   * "Freigegeben", wo der Knopf steht -- eine Wegbeschreibung statt eines
   * Wegs. Der Dialog wird nicht hier gebaut, sondern von `Workbench.tsx`
   * hereingereicht: es gibt genau einen `useUploadJob` je Seite.
   */
  onUpload?: () => void;
}

export function WorkbenchEmptyState({
  variant,
  sessionAccepted = 0,
  sessionRejected = 0,
  onResetFilters,
  approvedAwaitingUpload = 0,
  justBulkReleased = 0,
  approvedTotal = null,
  avgScore = null,
  onUpload,
}: WorkbenchEmptyStateProps) {
  const { t } = useTranslation('workbench');
  const { leerzustand } = useEntwurf();

  if (variant === 'no-feed') {
    /**
     * Bis Task 11 stand hier "geh zurueck zur Startseite, um einen Feed zu
     * waehlen" plus ein Navigations-Knopf. Das war damals richtig: den
     * Feed-Umschalter gab es nur auf dem Feed-Check und dem
     * Performance-Dashboard, die Werkbank war in diesem Zustand knopflos.
     *
     * Seit der Selektor in der Kopfzeile ueber JEDER Seite steht, ist es eine
     * falsche Anweisung — die verlangte Handlung ist zwei Zeilen weiter oben
     * moeglich. Der Knopf ist deshalb weg (er waere der laengere von zwei
     * Wegen zur selben Handlung, Muster 'allReviewed'), und die Beschreibung
     * zeigt nach OBEN statt weg (Muster `optimization:selectFeedDesc`).
     */
    return (
      <EmptyState
        paperCutIcon="quality-score"
        title={t('emptyState.noFeed.title')}
        description={t('emptyState.noFeed.description')}
      />
    );
  }

  /**
   * Leergefiltertes Ergebnis: es GIBT offene Produkte, nur passt keines zur
   * aktiven Suche bzw. zum Score-Filter. Die frueher hier gerenderte
   * 'no-optimized'-Variante behauptete "Noch nichts zu pruefen — optimiere
   * deine Produkte" und schickte den Nutzer per Primaerknopf aus der Werkbank
   * hinaus. Beide Aussagen waren im Filterfall falsch. Einzige Handlung:
   * Filter zuruecksetzen (Signal kommt aus dem Filter-State, nicht aus
   * Sitzungszaehlern).
   */
  if (variant === 'no-filter-results') {
    return (
      <EmptyState
        paperCutIcon="quality-score"
        title={t('emptyState.noFilterResults.title')}
        description={t('emptyState.noFilterResults.description')}
        actionLabel={t('emptyState.noFilterResults.resetFilters')}
        onAction={onResetFilters}
      />
    );
  }

  /**
   * Der Zwilling von 'no-optimized': von aussen identisch (nichts zu pruefen,
   * leere Sitzung), inhaltlich das Gegenteil. Wer ueber Schritt 4 der
   * Fortschrittsleiste hier ankommt, hat alles freigegeben und wartet auf den
   * Upload. Die 'no-optimized'-Copy ("Optimiere deine Produkte") waere hier
   * schlicht falsch, und ihr Primaerknopf konkurrierte mit dem Upload-Knopf im
   * Seitenkopf.
   *
   * Bewusst OHNE eigenen Knopf: der Primaer-CTA dieses Zustands steht im
   * Seitenkopf (Verbot 10, genau einer pro Zustand). Ein zweiter waere
   * entweder Konkurrenz oder eine Umleitung ueber die Startseite.
   */
  if (variant === 'approved-awaiting-upload') {
    /*
     * Mockup C2Danach nennt hier drei Dinge, die der Text bis zum Audit nicht
     * hatte: die ZAHL der wartenden Produkte, die Zusicherung "bis dahin
     * aendert sich fuer deine Kunden nichts" (die eigentliche Frage nach einer
     * Freigabe ueber tausende Produkte) und die Herkunft der Zahl.
     *
     * Die Herkunftszeile steht nur, wenn gerade eine Sammelfreigabe lief.
     * Nach 3.777 Freigaben springt die Kopfzahl auf 4.189, und ohne sie ist
     * nicht zu sehen, woher die restlichen 412 kommen. Waren es keine
     * vorherigen, faellt der zweite Halbsatz weg statt "0" zu behaupten.
     */
    const vorherSchon = Math.max(0, approvedAwaitingUpload - justBulkReleased);
    return (
      <EmptyState
        paperCutIcon="upload-import"
        title={t('emptyState.approvedAwaitingUpload.title')}
        description={t('emptyState.approvedAwaitingUpload.description', {
          count: approvedAwaitingUpload,
        })}
      >
        {/* Uebergabe (Spec 2026-09-16, Abschnitt 7): der naechste Schritt ist
            der Upload, und er passiert HIER statt in einem anderen Reiter. */}
        {onUpload && approvedAwaitingUpload > 0 && (
          <div className="mb-4">
            <NextStepCta step="upload" from="approve" count={approvedAwaitingUpload} onAction={onUpload} />
          </div>
        )}
        {justBulkReleased > 0 ? (
          <p className="text-sm text-muted-foreground">
            {vorherSchon > 0
              ? t('emptyState.approvedAwaitingUpload.fromBulkWithPrior', {
                  count: justBulkReleased,
                  vorher: vorherSchon,
                })
              : t('emptyState.approvedAwaitingUpload.fromBulk', { count: justBulkReleased })}
          </p>
        ) : null}
        {/* Erfolgsmoment (Entwurfs-Schalter): die Arbeit steht als Zahl im
            Bild statt nur im Fliesstext. Beide Quellen sind ehrlich optional
            - fehlt eine, faellt ihre Zeile weg, und fehlen beide, gibt es
            keine leere Huelse. Erfunden wird hier nichts. */}
        {leerzustand === 'erfolgsmoment' &&
        ((approvedTotal !== null && approvedTotal > 0) || avgScore !== null) ? (
          <div
            data-testid="erfolgsmoment-stats"
            className="mb-4 flex flex-wrap items-center justify-center gap-2"
          >
            {approvedTotal !== null && approvedTotal > 0 && (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-success-light px-3 py-1.5 text-sm font-medium text-success-ink">
                <Check className="h-4 w-4" aria-hidden="true" />
                {t('emptyState.erfolgsmoment.total', { count: approvedTotal })}
              </span>
            )}
            {avgScore !== null && (
              <span
                data-testid="erfolgsmoment-avg"
                className="inline-flex items-center gap-1.5 rounded-full bg-accent px-3 py-1.5 text-sm font-medium text-foreground"
              >
                {t('emptyState.erfolgsmoment.avg', { score: Math.round(avgScore) })}
              </span>
            )}
          </div>
        ) : null}
      </EmptyState>
    );
  }

  /*
   * Die beiden Leerzustaende der neuen Reiter (Task A7). Sie sind bewusst
   * ohne Primaerknopf: hier ist nichts kaputt und nichts zu tun. Ein CTA
   * muesste sich etwas ausdenken, und das Naheliegende ("geh zum Feed-Check")
   * fuehrt vom Ziel weg.
   */
  if (variant === 'no-approved') {
    return (
      <EmptyState
        paperCutIcon="quality-score"
        title={t('emptyState.noApproved.title')}
        description={t('emptyState.noApproved.description')}
      />
    );
  }

  if (variant === 'no-rejected') {
    return (
      <EmptyState
        paperCutIcon="quality-score"
        title={t('emptyState.noRejected.title')}
        description={t('emptyState.noRejected.description')}
      />
    );
  }

  if (variant === 'no-optimized') {
    return (
      <EmptyState
        paperCutIcon="quality-score"
        title={t('emptyState.noOptimized.title')}
        description={t('emptyState.noOptimized.description')}
      >
        <Button asChild variant="default">
          <Link to="/">{t('emptyState.noOptimized.goHome')}</Link>
        </Button>
      </EmptyState>
    );
  }

  return (
    <EmptyState
      paperCutIcon="quality-score"
      title={t('emptyState.allReviewed.title')}
      description={t('emptyState.allReviewed.description')}
    >
      <div className="flex flex-col gap-1 text-sm mb-5">
        {sessionAccepted > 0 && (
          <span className="text-success-ink font-medium">
            {t('emptyState.allReviewed.approved', { count: sessionAccepted })}
          </span>
        )}
        {sessionRejected > 0 && (
          <span className="text-muted-foreground">
            {t('emptyState.allReviewed.rejected', { count: sessionRejected })}
          </span>
        )}
      </div>
      {/* Seit dem 16.09.2026 (Spec Abschnitt 7) steht hier der Upload, WENN
          etwas freigegeben ist: "alles geprueft" ist genau der Moment, in dem
          der naechste Schritt ansteht, und er soll nicht erst im Seitenkopf
          gesucht werden muessen. Der Weg zurueck ins Dashboard bleibt als
          Nebenweg -- und ist ohne freigegebene Menge der einzige Knopf
          (Verbot 10: nie zwei gefuellte Flaechen). */}
      {onUpload && approvedAwaitingUpload > 0 && (
        <div className="mb-3">
          <NextStepCta step="upload" from="approve" count={approvedAwaitingUpload} onAction={onUpload} />
        </div>
      )}
      <Button asChild variant="outline">
        <Link to="/">{t('emptyState.allReviewed.goHome')}</Link>
      </Button>
    </EmptyState>
  );
}
